import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './IncluirJanta.css';

/**
 * Página para Admin gerenciar janta
 */

const IncluirJanta = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [custoJanta, setCustoJanta] = useState('');
  const [participantesJanta, setParticipantesJanta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);
      setCustoJanta(aberta.custo_janta ? parseFloat(aberta.custo_janta).toFixed(2) : '');

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      setJogadoresConfirmados(confirmacoes);

      // Carregar participantes da janta
      const rodadaCompletaRes = await apiClient.get(`/rodadas/${aberta.id}`);
      const participacoes = rodadaCompletaRes.data.participacoes || [];
      const participantes = participacoes
        .filter(p => p.participou_janta)
        .map(p => p.jogador_id);
      setParticipantesJanta(participantes);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleParticipante = (jogadorId) => {
    if (participantesJanta.includes(jogadorId)) {
      setParticipantesJanta(participantesJanta.filter(id => id !== jogadorId));
    } else {
      setParticipantesJanta([...participantesJanta, jogadorId]);
    }
  };

  const handleSalvar = async (e) => {
    e.preventDefault();

    if (participantesJanta.length > 0 && !custoJanta) {
      setError('Informe o custo da janta');
      return;
    }

    try {
      setSalvando(true);
      setError('');

      await apiClient.put(`/rodadas/${rodadaAberta.id}/janta`, {
        custo_janta: custoJanta ? parseFloat(custoJanta) : null,
        participantes_janta: participantesJanta,
      });

      alert('Janta atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar janta:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar janta');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  const custoPorPessoa = participantesJanta.length > 0 && custoJanta
    ? (parseFloat(custoJanta) / participantesJanta.length).toFixed(2)
    : '0.00';

  return (
    <div className="container incluir-janta-container">
      <div className="page-header">
        <h1>🍽️ Gerenciar Janta</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSalvar} className="janta-form">
        <div className="form-group">
          <label>Custo Total da Janta (R$)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={custoJanta}
            onChange={(e) => setCustoJanta(e.target.value)}
            placeholder="0.00"
          />
          <small>Informe o valor total gasto com a janta</small>
        </div>

        <div className="form-group">
          <label>Participantes da Janta</label>
          <div className="participantes-list">
            {jogadoresConfirmados.map(c => {
              const participou = participantesJanta.includes(c.jogador_id);
              return (
                <label key={c.jogador_id} className="participante-item">
                  <input
                    type="checkbox"
                    checked={participou}
                    onChange={() => handleToggleParticipante(c.jogador_id)}
                  />
                  <span>{c.jogador?.nome || 'Jogador'}</span>
                  {participou && custoJanta && (
                    <span className="custo-individual">
                      R$ {custoPorPessoa}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <small>
            {participantesJanta.length} participante(s) selecionado(s)
          </small>
        </div>

        {participantesJanta.length > 0 && custoJanta && (
          <div className="resumo-janta">
            <h3>Resumo</h3>
            <p><strong>Total:</strong> R$ {parseFloat(custoJanta).toFixed(2)}</p>
            <p><strong>Participantes:</strong> {participantesJanta.length}</p>
            <p><strong>Por pessoa:</strong> R$ {custoPorPessoa}</p>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={salvando} className="btn btn-primary">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IncluirJanta;
